import React from 'react';
import { AddRowButton } from '#@itinerary/planner/row.jsx';
import { Icon } from '@iconify-icon/react';
import FloatingTooltip from '#components/tooltip.jsx';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Button from '#lib/components/button.jsx';
import { buttonLooks, sizes } from '#lib/styles/constants.js';
import Input from '#lib/components/input.jsx';

export default function UsersList(props) {
    const {
        users,
        className,
        selectedUserId,
        availableUsers,
        totalUsers,
        onAdd,
        onDelete,
        onQuery,
        onSelect,
        goToBilling,
        setQuery,
        query
    } = props;

    const handleAddUser = () => {
        if (hasNewUser) {
            return toast.error(_t.users.newButtonTooltip());
        }
        onAdd();
    };

    const handleDeleteUser = (user) => {
        const confirm = window.confirm(_t.global.confirmDelete());
        if (!confirm) return;
        onDelete(user);
    };

    if (!Array.isArray(users)) return null;

    const hasNewUser = Array.isArray(users) && users.some((el) => el.isNew);

    return (
        <div
            className={mergeClasses(
                cn('min-w-[350px] w-full md:max-w-[500px] pr-3 mb-4 lg:mb-0 flex flex-col p-2 bg-neutral-100'),
                className
            )}
        >
            <div className="flex gap-2 items-end pb-2">
                <Input.Full
                    className="w-full"
                    type="search"
                    value={query}
                    placeholder={_t.intl.filterLabel()}
                    onChange={(e) => setQuery(e?.target?.value)}
                    autoComplete="off"
                />
            </div>
            <ul className="w-full overflow-auto h-auto max-h-[87%] flex flex-col divide-y divide-neutral-200 border border-neutral-200 mb-2 rounded">
                {users.map((user) => {
                    const selected = selectedUserId === user.id;
                    const name =
                        user.firstName || user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : `${_t.users.firstName().toLowerCase()} ${_t.users.lastName().toLowerCase()}`;
                    const email = user.email || _t.users.email().toLowerCase();

                    return (
                        <li
                            key={user.id}
                            className={cn(
                                'pl-2 py-1 pr-1 w-full flex items-center cursor-pointer justify-between bg-white hover:bg-neutral-200',
                                user.isNew && 'bg-primary-100 pl-1'
                            )}
                            onClick={() => onSelect(user.id)}
                        >
                            <div className="flex w-4/5 gap-5 items-center">
                                <span
                                    className={cn(
                                        'overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold',
                                        selected && user.firstName && user.lastName && user.email && 'text-primary-600',
                                        selected &&
                                            !(user.firstName && user.lastName && user.email) &&
                                            'text-neutral-500'
                                    )}
                                >
                                    {`${name} (${email})`}
                                </span>
                            </div>
                            <div className="flex w-1/5 justify-end">
                                {user.main ? (
                                    <FloatingTooltip content={_t.users.mainLabel()} placement="top">
                                        <Icon
                                            icon="line-md:person"
                                            width="1.4rem"
                                            height="1.4rem"
                                            className="pt-[5px] cursor-default"
                                        />
                                    </FloatingTooltip>
                                ) : (
                                    <>
                                        {user.enabled && (
                                            <FloatingTooltip content={_t.users.enabledCheckbox()} placement="top">
                                                <Icon
                                                    icon="lets-icons:check-fill"
                                                    width="1.4rem"
                                                    height="1.4rem"
                                                    className="pt-1 mr-2 cursor-default text-primary-600"
                                                />
                                            </FloatingTooltip>
                                        )}
                                        <FloatingTooltip content={_t.global.delete()} placement="top">
                                            <Icon
                                                icon="ph:trash"
                                                width="1.3rem"
                                                height="1.3rem"
                                                className="pt-[5px] cursor-pointer hover:text-primary-600"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteUser(user);
                                                }}
                                            />
                                        </FloatingTooltip>
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
            <div className="mt-3">
                {availableUsers !== 0 && (
                    <span className="mt-3 ">
                        <AddRowButton
                            onClick={handleAddUser}
                            className={addClasses(
                                cn(hasNewUser && 'cursor-default pointer-events-none !text-neutral-300')
                            )}
                        />
                    </span>
                )}
                {availableUsers === 0 && (
                    <span className="mt-3">
                        <BuyMoreButton onClick={goToBilling}>{_t.users.buyMoreUsers()}</BuyMoreButton>
                    </span>
                )}
                {availableUsers !== -1 && (
                    <span className="flex justify-between mt-3">
                        <span className="text-lg text-neutral-500">
                            {_t.users.usersAvailable(availableUsers, totalUsers)}
                        </span>
                        <span onClick={goToBilling} className="cursor-pointer text-lg text-primary-600 hover:underline">
                            {_t.users.buyMoreUsers(availableUsers, totalUsers)}
                        </span>
                    </span>
                )}
            </div>
        </div>
    );
}

export function BuyMoreButton(props) {
    const { children, className, ...rest } = props;
    return (
        <Button
            className={addClasses(
                mergeClasses(
                    'relative w-full flex justify-center items-center gap-x-2 px-4 py-1 mb-0 text-neutral-600 rounded border-2 cursor-pointer',
                    className
                )
            )}
            look={buttonLooks.primary}
            size={sizes.sm}
            type="button"
            {...rest}
        >
            <div className="h-full flex items-center">{children || _t.global.buyMore()}</div>
            <div className="h-full flex items-center">
                <Icon icon="fluent:arrow-right-12-filled" width="1.2rem" height="1.2rem" /* className="text-white" */ />
            </div>
        </Button>
    );
}
